import React, { useState, useEffect } from 'react';
import {
  ITEM_SNOOZE_PRESETS,
  DEFAULT_ITEM_SNOOZE_PRESET_KEY,
  MAX_SNOOZE_HELPER_TEXT,
  maxSnoozeDate,
} from '../../ItemAvailability/itemSnoozePresets';
import { VariantScopeList } from '../../ItemAvailability/VariantScopeList';
import {
  Box,
  Typography,
  Alert,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTimeFormat } from '../../../context/TimeFormatContext';
import { formatSnoozeDateTime, PICKER_DATE_TIME_FORMAT } from '../../../utils/formatTime';
import {
  searchProducts,
  searchBundles,
  searchModifiers,
  searchOptionValues
} from '../../../api/products';
import type {
  ProductSearchResult,
  BundleSearchResult,
  VariantSearchResult,
  ModifierSearchResult,
  OptionValueSearchResult
} from '../../../api/products';
import type { BulkItemsEditorProps } from './types';

type SearchResult = ProductSearchResult | BundleSearchResult | VariantSearchResult | ModifierSearchResult | OptionValueSearchResult;

export const BulkItemsEditor: React.FC<BulkItemsEditorProps> = ({
  itemsOperation,
  onOperationChange,
  selectedItems,
  onSelectedItemsChange,
  itemType,
  onItemTypeChange,
  itemExpiry,
  onItemExpiryChange,
  variantScope,
}) => {
  const { use24HourFormat } = useTimeFormat();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedQuickOption, setSelectedQuickOption] =
    useState<string>(DEFAULT_ITEM_SNOOZE_PRESET_KEY);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, itemType]);

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      let results: SearchResult[] = [];

      switch (itemType) {
        case 'PRODUCT':
          results = await searchProducts(query);
          break;
        case 'BUNDLE':
          results = await searchBundles(query);
          break;
        case 'MODIFIER':
          results = await searchModifiers(query);
          break;
        case 'OPTION_VALUE':
          results = await searchOptionValues(query);
          break;
        // VARIANT removed from the Type dropdown — variants are now reached
        // by drilling into a product.
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Manage item availability across all selected stores.
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Operation
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            label="Snooze Item"
            onClick={() => onOperationChange('snooze')}
            color={itemsOperation === 'snooze' ? 'warning' : 'default'}
            variant={itemsOperation === 'snooze' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Activate Item"
            onClick={() => onOperationChange('activate')}
            color={itemsOperation === 'activate' ? 'success' : 'default'}
            variant={itemsOperation === 'activate' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      <Stack spacing={2}>
        {/* Type Selector */}
        <FormControl fullWidth>
          <InputLabel>Type</InputLabel>
          <Select
            value={itemType}
            onChange={(e) => {
              onItemTypeChange(e.target.value as typeof itemType);
              onSelectedItemsChange([]);
              setSearchQuery('');
              setSearchResults([]);
            }}
            label="Type"
          >
            <MenuItem value="PRODUCT">Product</MenuItem>
            <MenuItem value="BUNDLE">Bundle</MenuItem>
            <MenuItem value="MODIFIER">Modifier</MenuItem>
          </Select>
        </FormControl>

        {/* Autocomplete Search - Multi-select */}
        <Autocomplete
          multiple
          value={selectedItems.map(item => ({
            code: item.code,
            name: item.name,
            label: `${item.code} - ${item.name}`
          }))}
          onChange={(_, newValue) => {
            const updatedItems = newValue.map(item => ({
              type: itemType,
              code: item.code,
              name: item.name
            }));
            onSelectedItemsChange(updatedItems);
          }}
          inputValue={searchQuery}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === 'input') {
              setSearchQuery(newInputValue);
            }
          }}
          options={searchResults}
          getOptionLabel={(option) => option.label || `${option.code} - ${option.name}`}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          disableCloseOnSelect
          loading={isSearching}
          loadingText="Searching..."
          noOptionsText={searchQuery.length < 2 ? `Type to search ${itemType}` : 'No results found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${itemType} Code *`}
              placeholder={selectedItems.length === 0 ? `Search for ${itemType.toLowerCase()}s...` : ''}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.code}
                label={option.name}
                size="small"
              />
            ))
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...otherProps } = props as any;
            return (
              <Box component="li" key={key} {...otherProps}>
                <Checkbox
                  checked={selected}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body1">{option.label || `${option.code} - ${option.name}`}</Typography>
              </Box>
            );
          }}
        />

        {/* Per-product variant drill-down — applies to both snooze and
            activate. Each selected product keeps its own scope. */}
        <VariantScopeList
          scope={variantScope}
          presentation="desktop"
          verb={itemsOperation === 'snooze' ? 'Snoozing' : 'Activating'}
        />

        {itemsOperation === 'snooze' && (
          <>
            {/* Quick Expiry Options — shared with the desktop and mobile pages */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick Options (Store Local Time)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1, mb: 2 }}>
                {ITEM_SNOOZE_PRESETS.map((preset) => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    onClick={() => {
                      onItemExpiryChange(preset.getValue());
                      setSelectedQuickOption(preset.key);
                    }}
                    variant={selectedQuickOption === preset.key ? 'filled' : 'outlined'}
                    color={selectedQuickOption === preset.key ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>

            {/* Expiry Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                format={PICKER_DATE_TIME_FORMAT}
                label="Expires At *"
                value={itemExpiry}
                onChange={(newValue) => {
                  onItemExpiryChange(newValue);
                  setSelectedQuickOption('');
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: itemExpiry
                      ? `Items reactivate: ${formatSnoozeDateTime(itemExpiry, use24HourFormat)}. ${MAX_SNOOZE_HELPER_TEXT}`
                      : `Items will be automatically reactivated at this time. ${MAX_SNOOZE_HELPER_TEXT}`,
                    required: true,
                  }
                }}
                minDateTime={new Date()}
                // Byte Graph rejects expiries past the ceiling — grey them out.
                maxDateTime={maxSnoozeDate()}
              />
            </LocalizationProvider>
          </>
        )}
      </Stack>
    </Box>
  );
};
